import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, parseFieldErrors, resetSessionCache } from "@/lib/queryClient";
import { consumeReturnTo } from "@/lib/auth-return-to";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import { applyThemeClass } from "@/lib/theme";
import { JOURNEY_STAGES, JOURNEY_STAGE_LABELS, type JourneyStage } from "@shared/schema";
import { getStoredReferralCode } from "@/lib/referral-capture";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Modular pricing: signups pick an ACCOUNT KIND (operator vs. property
// manager), not a legacy tier. The base plan + modules are configured on the
// onboarding payment step (the plan calculator); prices there.
const ACCOUNT_KIND_OPTIONS = [
  {
    value: "operator" as const,
    label: "Operator",
    priceLabel: "I run my own co-living portfolio",
  },
  {
    value: "property_manager" as const,
    label: "Property Manager",
    priceLabel: "I manage properties for owners",
  },
] as const;

const COUNTRY_CODES = [
  { code: "+1", label: "US +1" },
  { code: "+44", label: "UK +44" },
  { code: "+91", label: "IN +91" },
  { code: "+61", label: "AU +61" },
  { code: "+49", label: "DE +49" },
  { code: "+33", label: "FR +33" },
  { code: "+81", label: "JP +81" },
  { code: "+86", label: "CN +86" },
  { code: "+55", label: "BR +55" },
  { code: "+52", label: "MX +52" },
];

type LoginStep = "email" | "method-choice" | "enter-password";

const LOGIN_METHOD_KEY = (email: string) => `padpulse_login_method_${email.toLowerCase()}`;

export default function AppLogin() {
  usePageTitle("Sign In");
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    root.classList.remove("dark");
    root.classList.add("light");
    return () => {
      if (hadDark && root.classList.contains("light") && !root.classList.contains("dark")) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  const [sessionProbeState, setSessionProbeState] = useState<"checking" | "ready">("checking");
  const probingRef = useRef(false);
  const navigatedRef = useRef(false);
  const [, navigate] = useLocation();

  const probeSession = useCallback(async () => {
    if (probingRef.current || navigatedRef.current) return;
    probingRef.current = true;
    try {
      const res = await fetch("/api/app/profile", { credentials: "include" });
      if (res.ok && !navigatedRef.current) {
        navigatedRef.current = true;
        const profile = await res.json().catch(() => null);
        resetSessionCache();
        navigate(consumeReturnTo(profile?.id ?? null) ?? "/app/dashboard");
        return;
      }
    } catch {
      // Network error — fall through to show the form
    } finally {
      probingRef.current = false;
    }
    setSessionProbeState("ready");
  }, [navigate]);

  useEffect(() => {
    probeSession();
  }, [probeSession]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        probeSession();
      }
    };
    const handleFocus = () => {
      probeSession();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [probeSession]);

  const [mode, setMode] = useState<"login" | "signup">(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("mode") === "signup") return "signup";
    }
    return "login";
  });
  const [loginStep, setLoginStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordRateLimitUntil, setPasswordRateLimitUntil] = useState<number | null>(null);
  const [passwordRateLimitRemaining, setPasswordRateLimitRemaining] = useState(0);
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [journeyStage, setJourneyStage] = useState<JourneyStage | "">("");
  const [countryCode, setCountryCode] = useState("+1");
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [signupFieldErrors, setSignupFieldErrors] = useState<Record<string, string>>({});
  const [accountKind, setAccountKind] = useState<"operator" | "property_manager">(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("type") === "pm") return "property_manager";
    }
    return "operator";
  });
  const [estimatedRooms, setEstimatedRooms] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    const goPassword = sessionStorage.getItem("padpulse_auth_pw_redirect");
    const storedEmail = sessionStorage.getItem("padpulse_auth_email");
    if (goPassword === "1" && storedEmail) {
      setEmail(storedEmail);
      setLoginStep("enter-password");
      sessionStorage.removeItem("padpulse_auth_pw_redirect");
    }
  }, []);

  const clearSignupError = (field: string) => {
    setSignupFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const checkPasswordMutation = useMutation({
    mutationFn: async (loginEmail: string) => {
      const res = await apiRequest("POST", "/api/app/auth/check-password", { email: loginEmail });
      return res.json() as Promise<{ hasPassword: boolean }>;
    },
    onSuccess: (data, loginEmail) => {
      const trimmedEmail = loginEmail.toLowerCase();
      sessionStorage.setItem("padpulse_auth_has_password", data.hasPassword ? "1" : "0");
      const lastMethod = localStorage.getItem(LOGIN_METHOD_KEY(trimmedEmail));
      if (data.hasPassword && lastMethod === "password") {
        setLoginStep("enter-password");
      } else if (data.hasPassword) {
        setLoginStep("method-choice");
      } else {
        requestCodeMutation.mutate(loginEmail);
      }
    },
    onError: (_err, loginEmail) => {
      sessionStorage.setItem("padpulse_auth_has_password", "0");
      requestCodeMutation.mutate(loginEmail);
    },
  });

  const requestCodeMutation = useMutation({
    mutationFn: async (loginEmail: string) => {
      const res = await apiRequest("POST", "/api/app/auth/request-code", { email: loginEmail });
      return res.json();
    },
    onSuccess: (_data, loginEmail) => {
      const trimmedEmail = loginEmail.toLowerCase();
      localStorage.setItem(LOGIN_METHOD_KEY(trimmedEmail), "email-code");
      sessionStorage.setItem("padpulse_auth_email", trimmedEmail);
      sessionStorage.removeItem("padpulse_signup_data");
      navigate("/app/verify");
    },
    onError: (error: Error) => {
      const msg = error.message || "";
      let description = "Please try again.";
      let title = "Something went wrong";
      try {
        const parsed = JSON.parse(msg.includes(":") ? msg.split(": ").slice(1).join(": ") : msg);
        if (parsed.code === "WAITLIST_ONLY") {
          title = "You're on the waitlist";
          description = parsed.message || "We'll notify you when access is ready.";
        } else if (parsed.code === "ACCOUNT_SUSPENDED") {
          title = "Account suspended";
          description = parsed.message || "Please contact support.";
        } else if (parsed.code === "RATE_LIMITED") {
          title = "Too many requests";
          description = parsed.message || "Please wait before trying again.";
        } else if (parsed.message) {
          description = parsed.message;
        }
      } catch {}
      toast({ title, description, variant: "destructive" });
    },
  });

  const loginPasswordMutation = useMutation({
    mutationFn: async ({ loginEmail, loginPassword }: { loginEmail: string; loginPassword: string }) => {
      const res = await apiRequest("POST", "/api/app/auth/login-password", { email: loginEmail, password: loginPassword });
      return res.json();
    },
    onSuccess: (data, { loginEmail }) => {
      const trimmedEmail = loginEmail.toLowerCase();
      localStorage.setItem(LOGIN_METHOD_KEY(trimmedEmail), "password");
      if (data.requiresTotpEnrollment) {
        navigate("/app/totp-enroll");
        return;
      }
      if (data.requiresTotp) {
        navigate("/app/totp-verify");
        return;
      }
      sessionStorage.removeItem("padpulse_signup_data");
      resetSessionCache();
      const theme = data?.user?.preferredTheme;
      if (theme === "dark" || theme === "light" || theme === "system") {
        applyThemeClass(theme);
      }
      // Phase 3 — restore a stashed deep link (e.g. a notification tap that
      // arrived with an expired session) instead of the dashboard default.
      // The stash is bound to the user it was captured for.
      navigate(consumeReturnTo(data?.user?.id ?? null) ?? "/app/dashboard");
    },
    onError: (error: Error) => {
      const msg = error.message || "";
      let description = "Invalid email or password.";
      let code = "";
      let retryAfterMs = 0;
      try {
        const parsed = JSON.parse(msg.includes(":") ? msg.split(": ").slice(1).join(": ") : msg);
        if (parsed.message) description = parsed.message;
        if (parsed.code) code = parsed.code;
        if (typeof parsed.retryAfterMs === "number") retryAfterMs = parsed.retryAfterMs;
      } catch {}
      if (code === "RATE_LIMITED") {
        if (retryAfterMs > 0) {
          setPasswordRateLimitUntil(Date.now() + retryAfterMs);
          setPasswordRateLimitRemaining(Math.ceil(retryAfterMs / 1000));
        }
        toast({ title: "Too many attempts", description, variant: "destructive" });
        return;
      }
      if (code === "ACCOUNT_SUSPENDED") {
        toast({ title: "Account suspended", description, variant: "destructive" });
        return;
      }
      setPasswordError(description);
    },
  });

  const [waitlistConfirmed, setWaitlistConfirmed] = useState(false);

  useEffect(() => {
    if (!passwordRateLimitUntil) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((passwordRateLimitUntil - Date.now()) / 1000));
      setPasswordRateLimitRemaining(remaining);
      if (remaining <= 0) {
        setPasswordRateLimitUntil(null);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [passwordRateLimitUntil]);

  const isPasswordRateLimited = passwordRateLimitUntil !== null && passwordRateLimitRemaining > 0;
  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m > 0) return `${m}:${String(s).padStart(2, "0")}`;
    return `${s}s`;
  };

  const signupMutation = useMutation({
    mutationFn: async (data: { fullName: string; email: string; phone: string; smsConsent: true; acceptedTerms: true; company: string; journeyStage: JourneyStage; accountKind: "operator" | "property_manager"; totalRooms?: number }) => {
      const res = await apiRequest("POST", "/api/app/auth/signup", data);
      return res.json() as Promise<{ success: boolean; status: string; message?: string }>;
    },
    onSuccess: (data) => {
      if (data.status === "on_list") {
        setWaitlistConfirmed(true);
        return;
      }
      sessionStorage.setItem("padpulse_auth_email", email.trim().toLowerCase());
      const referralCode = getStoredReferralCode();
      // Carry the pricing-page calculator configuration (if the prospect
      // arrived from /pricing) through to the onboarding payment step.
      const arrivalParams = new URLSearchParams(window.location.search);
      if (arrivalParams.get("intent") === "modular") {
        sessionStorage.setItem("padpulse_pending_plan_selection", arrivalParams.toString());
      }
      sessionStorage.setItem("padpulse_signup_data", JSON.stringify({
        fullName: fullName.trim(),
        phone: `${countryCode}${phone.replace(/\D/g, "")}`,
        smsConsent: true,
        acceptedTerms: true,
        company: company.trim(),
        journeyStage,
        accountKind,
        ...(estimatedRooms ? { totalRooms: parseInt(estimatedRooms, 10) } : {}),
        ...(referralCode ? { referralCode } : {}),
      }));
      navigate("/app/verify");
    },
    onError: (error: Error) => {
      const fieldErrors = parseFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setSignupFieldErrors(fieldErrors);
        toast({
          title: "Please fix the highlighted fields",
          description: Object.values(fieldErrors).join(" "),
          variant: "destructive",
        });
        return;
      }
      const msg = error.message?.includes(":") ? error.message.split(": ").slice(1).join(": ") : error.message;
      let description = "Please try again.";
      let code = "";
      try {
        const parsed = JSON.parse(msg);
        if (parsed.message) description = parsed.message;
        if (parsed.code) code = parsed.code;
      } catch {
        if (msg) description = msg;
      }
      if (code === "ACCOUNT_EXISTS") {
        toast({ title: "Account exists", description: "An account with that email already exists. Please log in instead." });
        setMode("login");
        return;
      }
      toast({ title: "Signup failed", description, variant: "destructive" });
    },
  });

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    checkPasswordMutation.mutate(email.trim());
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    setPasswordError("");
    loginPasswordMutation.mutate({ loginEmail: email.trim(), loginPassword: password });
  };

  const handleUseEmailCode = () => {
    setPassword("");
    setPasswordError("");
    requestCodeMutation.mutate(email.trim());
  };

  const handleBackToEmail = () => {
    setLoginStep("email");
    setPassword("");
    setPasswordError("");
  };

  const handleSignupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSignupFieldErrors({});
    const codeDigits = countryCode.replace(/\D/g, "");
    let cleanedPhone = phone.replace(/\D/g, "");
    if (codeDigits && cleanedPhone.startsWith(codeDigits) && cleanedPhone.length > 10) {
      cleanedPhone = cleanedPhone.slice(codeDigits.length);
    }
    if (!fullName.trim() || !email.trim()) return;
    if (!journeyStage) {
      setSignupFieldErrors({ journeyStage: "Please select where you are in your CoLiving journey" });
      return;
    }
    if (countryCode === "+1" && cleanedPhone.length !== 10) {
      setSignupFieldErrors({ phone: "Please enter a valid 10-digit mobile number including area code." });
      toast({ title: "Mobile phone is required", description: "Please enter a 10-digit mobile number including area code.", variant: "destructive" });
      return;
    }
    if (cleanedPhone.length < 7) {
      setSignupFieldErrors({ phone: "Please enter a valid mobile number." });
      toast({ title: "Mobile phone is required", description: "Please enter a valid mobile number.", variant: "destructive" });
      return;
    }
    if (!smsConsent) {
      toast({ title: "SMS consent required", description: "Please agree to receive SMS messages to continue.", variant: "destructive" });
      return;
    }
    if (!acceptedTerms) {
      setSignupFieldErrors((prev) => ({ ...prev, acceptedTerms: "You must agree to the Terms and Conditions." }));
      toast({ title: "Terms required", description: "Please agree to the Terms and Conditions to continue.", variant: "destructive" });
      return;
    }
    const roomCount = estimatedRooms.trim();
    if (!roomCount || isNaN(parseInt(roomCount, 10)) || parseInt(roomCount, 10) < 0) {
      setSignupFieldErrors((prev) => ({ ...prev, rooms: "Please enter your estimated active room count (enter 0 if none yet)." }));
      toast({ title: "Rooms required", description: "Please enter your estimated number of active rooms.", variant: "destructive" });
      return;
    }
    signupMutation.mutate({
      fullName: fullName.trim(),
      email: email.trim(),
      phone: `${countryCode}${cleanedPhone}`,
      smsConsent: true,
      acceptedTerms: true,
      company: company.trim(),
      journeyStage,
      accountKind,
      ...(estimatedRooms ? { totalRooms: parseInt(estimatedRooms, 10) } : {}),
    });
  };

  const isPendingContinue = checkPasswordMutation.isPending || requestCodeMutation.isPending;

  if (sessionProbeState === "checking") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" data-testid="login-session-checking">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-orange-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <a href="/" data-testid="link-login-logo">
            <img src={logoImg} alt="PadPulse" className="h-[60px] w-auto cursor-pointer" data-testid="img-login-logo" />
          </a>
        </div>

        <Card className="w-full p-8 shadow-xl border border-gray-100 bg-white/80 backdrop-blur-sm">
          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => { setMode("login"); setLoginStep("email"); setPassword(""); setPasswordError(""); }}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                mode === "login"
                  ? "bg-white text-brand shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              data-testid="tab-login"
            >
              Login
            </button>
            <button
              onClick={() => setMode("signup")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                mode === "signup"
                  ? "bg-white text-brand shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              data-testid="tab-signup"
            >
              Sign Up
            </button>
          </div>

          {mode === "login" ? (
            <>
              {loginStep === "email" && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-widest text-brand mb-2" data-testid="text-host-login-label">Host Login</p>
                  <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="text-login-title">Welcome back</h1>
                  <p className="text-gray-500 text-sm mb-6" data-testid="text-login-subtitle">Enter your email to sign in to your account</p>
                  <form onSubmit={handleLoginSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                      <Input
                        type="email"
                        placeholder="m@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="border-gray-200"
                        autoFocus
                        data-testid="input-login-email"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-brand hover:bg-brand-hover text-white font-semibold"
                      disabled={isPendingContinue}
                      data-testid="button-login-go"
                    >
                      {isPendingContinue ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
                    </Button>
                  </form>
                </>
              )}

              {loginStep === "method-choice" && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-widest text-brand mb-2" data-testid="text-host-login-label">Host Login</p>
                  <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="text-login-title">Welcome back</h1>
                  <p className="text-gray-500 text-sm mb-6 truncate" data-testid="text-login-email-display">{email}</p>
                  <Button
                    type="button"
                    className="w-full bg-brand hover:bg-brand-hover text-white font-semibold"
                    disabled={requestCodeMutation.isPending}
                    onClick={() => requestCodeMutation.mutate(email.trim())}
                    data-testid="button-send-email-code"
                  >
                    {requestCodeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email me a code"}
                  </Button>
                  <div className="mt-4 text-center space-y-2">
                    <button
                      type="button"
                      onClick={() => setLoginStep("enter-password")}
                      className="text-sm text-brand hover:underline block w-full"
                      data-testid="button-use-password-instead"
                    >
                      Use password instead
                    </button>
                    <button
                      type="button"
                      onClick={handleBackToEmail}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mx-auto"
                      data-testid="button-back-to-email"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" /> Use a different email
                    </button>
                  </div>
                </>
              )}

              {loginStep === "enter-password" && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-widest text-brand mb-2" data-testid="text-host-login-label">Host Login</p>
                  <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="text-login-title">Enter your password</h1>
                  <p className="text-gray-500 text-sm mb-6 truncate" data-testid="text-login-email-display-pw">{email}</p>
                  <form onSubmit={handlePasswordSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
                          className={`border-gray-200 pr-10 ${passwordError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                          autoFocus
                          disabled={isPasswordRateLimited}
                          data-testid="input-login-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          tabIndex={-1}
                          data-testid="button-toggle-password"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {passwordError && !isPasswordRateLimited && (
                        <p className="text-xs text-red-600 mt-1" data-testid="error-login-password">{passwordError}</p>
                      )}
                      {isPasswordRateLimited && (
                        <div
                          className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                          role="status"
                          aria-live="polite"
                          data-testid="text-password-rate-limit"
                        >
                          Too many attempts. Try again in <span className="font-semibold tabular-nums" data-testid="text-password-rate-limit-countdown">{formatCountdown(passwordRateLimitRemaining)}</span>.
                        </div>
                      )}
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-brand hover:bg-brand-hover text-white font-semibold"
                      disabled={loginPasswordMutation.isPending || isPasswordRateLimited}
                      data-testid="button-login-password-submit"
                    >
                      {loginPasswordMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : isPasswordRateLimited ? (
                        `Try again in ${formatCountdown(passwordRateLimitRemaining)}`
                      ) : (
                        "Sign in"
                      )}
                    </Button>
                  </form>
                  <div className="mt-4 text-center space-y-2">
                    <button
                      type="button"
                      onClick={handleUseEmailCode}
                      className="text-sm text-brand hover:underline block w-full"
                      disabled={requestCodeMutation.isPending}
                      data-testid="button-use-email-code-instead"
                    >
                      {requestCodeMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" /> : null}
                      Use email code instead
                    </button>
                    <button
                      type="button"
                      onClick={handleBackToEmail}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mx-auto"
                      data-testid="button-back-to-email-from-pw"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" /> Use a different email
                    </button>
                  </div>
                </>
              )}
            </>
          ) : waitlistConfirmed ? (
            <div className="text-center py-6" data-testid="waitlist-confirmation">
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank you for creating an account!</h1>
              <p className="text-gray-500 text-sm mb-4">Your account is being reviewed — you'll receive a scheduled onboarding date in the next 24 days.</p>
              <a href="https://padpulse.io" className="inline-block">
                <Button variant="outline" className="border-gray-200" data-testid="button-waitlist-visit">Visit padpulse.io</Button>
              </a>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="text-signup-title">Create your account</h1>
              <p className="text-gray-500 text-sm mb-6" data-testid="text-signup-subtitle">Sign up for a free PadPulse account</p>
              <form onSubmit={handleSignupSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                  <Input
                    type="text"
                    placeholder="John Smith"
                    value={fullName}
                    onChange={(e) => { setFullName(e.target.value); clearSignupError("fullName"); }}
                    className={`border-gray-200 ${signupFieldErrors.fullName ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    autoFocus
                    data-testid="input-signup-fullname"
                  />
                  {signupFieldErrors.fullName && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-signup-fullname">{signupFieldErrors.fullName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                  <Input
                    type="email"
                    placeholder="m@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); clearSignupError("email"); }}
                    className={`border-gray-200 ${signupFieldErrors.email ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    data-testid="input-signup-email"
                  />
                  {signupFieldErrors.email && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-signup-email">{signupFieldErrors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Company</label>
                  <Input
                    type="text"
                    placeholder="Your company (optional)"
                    value={company}
                    onChange={(e) => { setCompany(e.target.value); clearSignupError("company"); }}
                    className={`border-gray-200 ${signupFieldErrors.company ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    data-testid="input-signup-company"
                  />
                  {signupFieldErrors.company && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-signup-company">{signupFieldErrors.company}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Mobile phone *</label>
                  <div className="flex gap-2">
                    <select
                      value={countryCode}
                      onChange={(e) => { setCountryCode(e.target.value); clearSignupError("phone"); }}
                      className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white"
                      data-testid="select-signup-country-code"
                    >
                      {COUNTRY_CODES.map((cc) => (
                        <option key={cc.code} value={cc.code}>{cc.label}</option>
                      ))}
                    </select>
                    <Input
                      type="tel"
                      inputMode="tel"
                      placeholder="555 123 4567"
                      value={phone}
                      onChange={(e) => { setPhone(e.target.value); clearSignupError("phone"); }}
                      className={`border-gray-200 flex-1 ${signupFieldErrors.phone ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      data-testid="input-signup-phone"
                    />
                  </div>
                  {signupFieldErrors.phone && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-signup-phone">{signupFieldErrors.phone}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">CoLiving Journey *</label>
                  <Select
                    value={journeyStage || undefined}
                    onValueChange={(v) => { setJourneyStage(v as JourneyStage); clearSignupError("journeyStage"); }}
                  >
                    <SelectTrigger
                      className={`border-gray-200 ${signupFieldErrors.journeyStage ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      data-testid="select-signup-journey-stage"
                    >
                      <SelectValue placeholder="Select your stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {JOURNEY_STAGES.map((stage) => (
                        <SelectItem key={stage} value={stage} data-testid={`option-signup-journey-${stage}`}>
                          {JOURNEY_STAGE_LABELS[stage]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {signupFieldErrors.journeyStage && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-signup-journey-stage">{signupFieldErrors.journeyStage}</p>
                  )}
                </div>
                {/* Account kind — the plan itself is configured on the payment step */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What describes you best? *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {ACCOUNT_KIND_OPTIONS.map((kind) => (
                      <button
                        key={kind.value}
                        type="button"
                        onClick={() => setAccountKind(kind.value)}
                        className={`rounded-xl border p-3 text-left transition-all duration-200 ${
                          accountKind === kind.value
                            ? "border-brand bg-blue-50 ring-1 ring-brand/30"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                        data-testid={`card-account-type-${kind.value}`}
                      >
                        <div className="text-sm font-semibold text-gray-900">{kind.label}</div>
                        <div className="text-xs text-gray-500 mt-0.5 leading-tight">{kind.priceLabel}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Estimated rooms — required for all plans */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="signup-rooms">
                    Estimated active rooms *
                  </label>
                  <input
                    id="signup-rooms"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    placeholder="e.g. 12"
                    value={estimatedRooms}
                    onChange={(e) => { setEstimatedRooms(e.target.value); if (signupFieldErrors.rooms) clearSignupError("rooms"); }}
                    className={`flex h-9 w-full rounded-md border ${signupFieldErrors.rooms ? "border-red-500" : "border-gray-200"} bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
                    data-testid="input-signup-rooms"
                  />
                  <p className="mt-1.5 text-xs text-gray-500" data-testid="text-rooms-onboarding-hint">
                    Higher room volumes are prioritized for faster onboarding.
                  </p>
                  {signupFieldErrors.rooms && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-signup-rooms">{signupFieldErrors.rooms}</p>
                  )}
                </div>

                <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer select-none">
                  <Checkbox
                    checked={smsConsent}
                    onCheckedChange={(v) => setSmsConsent(v === true)}
                    className="mt-0.5"
                    data-testid="checkbox-signup-sms-consent"
                  />
                  <span>
                    I agree to receive transactional and marketing SMS messages from PadPulse at the number above. Message and data rates may apply. Reply STOP to opt out. See our{" "}
                    <a href="/terms#sms" target="_blank" className="text-brand underline">SMS Terms</a> and{" "}
                    <a href="/privacy" target="_blank" className="text-brand underline">Privacy Policy</a>.
                  </span>
                </label>
                <div>
                  <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer select-none">
                    <Checkbox
                      checked={acceptedTerms}
                      onCheckedChange={(v) => {
                        setAcceptedTerms(v === true);
                        if (v === true) clearSignupError("acceptedTerms");
                      }}
                      className={`mt-0.5 ${signupFieldErrors.acceptedTerms ? "border-red-500" : ""}`}
                      data-testid="checkbox-signup-accept-terms"
                    />
                    <span>
                      I agree to the{" "}
                      <a href="/terms" target="_blank" className="text-brand underline">Terms and Conditions</a>.
                    </span>
                  </label>
                  {signupFieldErrors.acceptedTerms && (
                    <p className="text-xs text-red-600 mt-1 ml-6" data-testid="error-signup-accept-terms">{signupFieldErrors.acceptedTerms}</p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-brand hover:bg-brand-hover text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={signupMutation.isPending || !acceptedTerms || !smsConsent}
                  data-testid="button-signup-submit"
                >
                  {signupMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
